export function nativeLoyaltySettings(restaurant: T.Schema.Restaurant.RestaurantSchema | null | undefined) {
  return restaurant?.settings?.loyalty_providers?.native;
}

export function isNativeLoyaltyConnected(
  restaurant: T.Schema.Restaurant.RestaurantSchema | null | undefined
): boolean {
  const native = nativeLoyaltySettings(restaurant);
  return !!(native?.merchant_id && native.location_id);
}

export function isNativeLoyaltyNavVisible(
  restaurant: T.Schema.Restaurant.RestaurantSchema | null | undefined
): boolean {
  const native = nativeLoyaltySettings(restaurant);
  return isNativeLoyaltyConnected(restaurant) && !!native?.enabled;
}

export function nativeLoyaltyMemberPoints(member: any, locationId?: string): number {
  if (member?.points?.balance != null) return Number(member.points.balance);
  const rows = member?.pointsByLocation;
  if (Array.isArray(rows) && locationId) {
    const row = rows.find((item: any) => item.locationId === locationId);
    if (row?.balance != null) return Number(row.balance);
  }
  if (typeof member?.points === 'number') return member.points;
  if (member?.pointsBalance != null) return Number(member.pointsBalance);
  return 0;
}

export function nativeLoyaltyGiftCardMinor(member: any): number {
  if (member?.giftCard?.balance != null) return Number(member.giftCard.balance);
  if (typeof member?.giftCard === 'number') return member.giftCard;
  return 0;
}

/** Formik 1.5.8 parses `<input type="number">` to a number (or '' if empty/NaN).
 * Settings save used to call `.trim()` and crash once Points Expiry / gift-card expiry were filled. */
export function optionalFormNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const s = value == null ? '' : String(value).trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function optionalFormInt(value: unknown): number | null {
  const n = optionalFormNumber(value);
  return n == null ? null : Math.trunc(n);
}

/** Major-unit <-> minor-unit conversion for the redemption "Value" field, mirroring
 * native-loyalty's own lib/money.ts (see that repo for why string-based, not
 * `Number(major) * 10 ** precision`: avoids float noise like 19.99 * 100 = 1998.9999999998).
 * `precision` is `restaurant.settings.region.currency.precision` (2 for AUD/USD, 0 for
 * VND/JPY, ...). */
export function minorToMajorString(minorUnits: number, precision: number): string {
  if (precision <= 0) return String(minorUnits);
  const factor = 10 ** precision;
  const sign = minorUnits < 0 ? '-' : '';
  const abs = Math.abs(minorUnits);
  const major = Math.floor(abs / factor);
  const minor = String(abs % factor).padStart(precision, '0');
  return `${sign}${major}.${minor}`;
}

export function majorStringToMinor(raw: string, precision: number): number | null {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '' || !/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const [majorPart, minorPartRaw = ''] = trimmed.split('.');
  if (precision <= 0) {
    if (minorPartRaw.length > 0 && Number(minorPartRaw) !== 0) return null;
    return Number(majorPart);
  }
  if (minorPartRaw.length > precision) return null;
  const minorPart = minorPartRaw.padEnd(precision, '0');
  return Number(majorPart) * 10 ** precision + Number(minorPart);
}

export const DEFAULT_LOYALTY_PROGRAM = {
  enabled: false,
  earn: {
    baseRate: 1,
    paymentMethodRates: {} as { [key: string]: number },
    minSpend: 0,
    rounding: 'up' as const,
    bonusMultiplier: 1,
    itemOverrides: [] as any[],
  },
  redemption: {
    redeemPoints: 100,
    redeemValue: 100,
    maxRedemptionPercent: 100,
    minRedemptionPoints: 0,
  },
  pointsExpiryDays: null as number | null,
  displayName: null as string | null,
  description: null as string | null,
  primaryColour: null as string | null,
  secondaryColour: null as string | null,
  goldTier: null as { minPoints: number; discountPercent: number } | null,
  birthdayDiscount: null as { discountPercent: number } | null,
};

export const DEFAULT_GIFT_CARD_PROGRAM = {
  enabled: false,
  expiryEnabled: false,
  defaultExpiryDays: null as number | null,
  purchaseEnabled: true,
  denominations: [5000, 10000, 15000, 20000] as number[],
  allowCustomAmount: false,
  minAmount: null as number | null,
  maxAmount: null as number | null,
  purchaseDiscountPercent: 0,
};
